import React, {ReactElement, useMemo} from 'react';
import {
  InfoCircleIcon,
  AlertWarningTriangleIcon,
  CheckCircleIcon,
  Link
} from '@customink/pigment-react';
import {Link as RouterLink} from 'react-router-dom';
import {selectPrimaryCampaign} from '../slices/storesSlice';
import {
  useGetStoreDetailsQuery,
  useGetOrderSummaryQuery,
  useGetBuyupQuery
} from '../api/storesApi';
import services from '../api/remoteServices';
import {
  useStoreSlug,
  useGroupOrderTimeline,
  useAppSelector,
  useFtux,
  useAppDispatch
} from './index';
import {showGroupOrderStatusDialog} from '../slices/appSlice';
import {selectAdmin} from '../slices/userSlice';

type QuantityStatusType = 'underQuantity' | 'equalQuantity' | 'overQuantity';
type OpenQuantityStatusType = 'zeroQuantity' | QuantityStatusType;

type DashboardMessageType = {
  icon: JSX.Element;
  title: string | ReactElement;
  message?: string | ReactElement;
};

export const useDashboardMessage = (): DashboardMessageType => {
  const dispatch = useAppDispatch();
  const urlSlug = useStoreSlug();
  const {refetch: refetchStoreDetails} = useGetStoreDetailsQuery(urlSlug);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const campaignId = primaryCampaign?.id;
  const {data: ccoUrl} = useGetBuyupQuery({urlSlug, campaignId});
  const isCampaignReopenable = primaryCampaign?.reopenable;
  const ftux = useFtux();
  const admin = useAppSelector(selectAdmin);

  const {closingSoon, daysUntilClose, autoCancelDateFormatted} =
    useGroupOrderTimeline();
  const {data: campaignOrderData} = useGetOrderSummaryQuery({
    urlSlug,
    campaignId
  });
  const daysUntilCloseMessage = daysUntilClose?.toLowerCase();

  const storeUrl = `/${urlSlug}`;
  const profileDesignsUrl = `${services.homePage}/profiles/designs`;
  const designLabUrl = `${services.homePage}/ndx`;
  const settingsUrl = `/${urlSlug}/manage/settings`;
  const inkerCheckoutUrl = `${services.stores}/${urlSlug}/campaigns/${campaignId}/checkout`;
  const isCampaignBuyupable =
    campaignOrderData?.buyupEnabled && campaignOrderData?.buyupable;

  const handleBuyupCompleteOrderClick = () => {
    refetchStoreDetails();
    dispatch(showGroupOrderStatusDialog(true));
  };

  const handleCompleteOrderClick = () => {
    window.location.href = admin ? inkerCheckoutUrl : ccoUrl;
  };

  const DASHBOARD_MESSAGES = {
    default: {
      icon: <InfoCircleIcon />,
      title: `It's time to start taking orders. Be the first to place an order or get your group involved.`
    },
    completedOrder: {
      icon: <CheckCircleIcon color="success" />,
      title: `This order is complete. Thanks so much for choosing Custom Ink! We hope you love your gear.`
    },
    canceledOrder: {
      icon: <InfoCircleIcon />,
      title: `This order was canceled.`,
      message: (
        <>
          <a href={profileDesignsUrl}>Start a new Group Order</a> or create a
          new design <a href={designLabUrl}>in the Design Lab</a>.
        </>
      )
    },
    openOrder: {
      notClosingSoon: {
        ftux: {
          icon: <InfoCircleIcon />,
          title: `Welcome to your dashboard. This is where you can view your orders, update your settings, and track your progress.`,
          message: `Put in the first order or share with your group to get going.`
        },
        zeroQuantity: {
          icon: <InfoCircleIcon />,
          title: `It's time to start taking orders. Be the first to place an order or get your group involved.`
        },
        underQuantity: {
          icon: <InfoCircleIcon />,
          title: `Looking good! Now that you have some orders in, you can review those orders and check your progress against your estimated quantity.`
        },
        equalQuantity: {
          icon: <InfoCircleIcon />,
          title: `Nice job! Your group hit your expected quantity.`,
          message: `You can keep your order open until your original close date or place your order now.`
        },
        overQuantity: {
          icon: <InfoCircleIcon />,
          title: `Look at you go! You're over your expected quantity.`,
          message: `You can keep your order open until your original close date or place your order now.`
        }
      },
      closingSoon: {
        ftux: {
          icon: <InfoCircleIcon />,
          title: `Welcome to your dashboard. This is where you can view your orders, update your settings, and track your progress.`,
          message: (
            <>
              Your order will close in {daysUntilCloseMessage}, so make sure to
              get your orders in quickly. If you need to extend your close date,
              you can do that in{' '}
              <RouterLink to={settingsUrl}>Settings</RouterLink>.
            </>
          )
        },
        zeroQuantity: {
          icon: <InfoCircleIcon />,
          title: (
            <>
              Your order will close in {daysUntilCloseMessage}, so make sure to
              get your orders in quickly. If you need to extend your close date,
              you can do that in{' '}
              <RouterLink to={settingsUrl}>Settings</RouterLink>.
            </>
          )
        },
        underQuantity: {
          icon: <InfoCircleIcon />,
          title: `Looking good! Now that you have some orders in, you can review those orders and check your progress against your estimated quantity.`,
          message: (
            <>
              Your order will close in {daysUntilCloseMessage}, so make sure to
              get your orders in quickly. If you need to extend your close date,
              you can do that in{' '}
              <RouterLink to={settingsUrl}>Settings</RouterLink>.
            </>
          )
        },
        equalQuantity: {
          icon: <CheckCircleIcon color="success" />,
          title: `Nice job! Your group hit your expected quantity.`,
          message: `You can keep your order open until your original close date or place your order now.`
        },
        overQuantity: {
          icon: <CheckCircleIcon color="success" />,
          title: `Look at you go! You're over your expected quantity.`,
          message: `You can keep your order open until your original close date or place your order now.`
        }
      }
    },
    closedOrder: {
      underQuantity: {
        icon: <AlertWarningTriangleIcon color="warning" />,
        title: `It looks like you didn't meet your expected quantity, but that's okay! You have a few options:`,
        message: (
          <>
            <ul>
              {isCampaignBuyupable ? (
                <li>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    onClick={handleBuyupCompleteOrderClick}
                    target="_blank"
                    rel="noreferrer">
                    Complete your order
                  </Link>
                  . Pay the difference in the per item price and send your gear
                  to production now
                </li>
              ) : null}
              <li>
                <RouterLink to={storeUrl} target="_blank" rel="noreferrer">
                  Buy more items
                </RouterLink>{' '}
                to meet your expected quantity and then come back here to
                complete your order (don&apos;t worry, you&apos;re the only one
                who still has access to your Group Order)
              </li>
              {isCampaignReopenable ? (
                <li>
                  <RouterLink to={settingsUrl}>Re-open your order</RouterLink>{' '}
                  to give yourself more time
                </li>
              ) : null}
              <li>
                <RouterLink to={settingsUrl}>Cancel</RouterLink> your Group
                Order
              </li>
            </ul>
            If you don&apos;t check out by {autoCancelDateFormatted}, we will
            automatically cancel your order.
          </>
        )
      },
      equalQuantity: {
        icon: <CheckCircleIcon color="success" />,
        title: (
          <>
            You did it! You got all of your orders in and now it&apos;s time to{' '}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link onClick={handleCompleteOrderClick}>complete your order</Link>.
          </>
        ),
        message: `If you don't check out by ${autoCancelDateFormatted}, we will automatically cancel your order.`
      },
      overQuantity: {
        icon: <CheckCircleIcon color="success" />,
        title: `You smashed it!`,
        message: (
          <>
            You can go ahead and{' '}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link onClick={handleCompleteOrderClick}>complete your order </Link>
            and we&apos;ll start getting your gear ready to go.
            <br />
            <br />
            If you don&apos;t check out by {autoCancelDateFormatted}, we will
            automatically cancel your order.
          </>
        )
      }
    }
  };

  const quantityStatus: OpenQuantityStatusType = useMemo(() => {
    const totalQuantity = primaryCampaign?.totalItemsQuantity;
    const expectedQuantity = primaryCampaign?.expectedQuantity;
    const storeOpen = storeDetails?.status === 'open' || false;

    if (storeOpen && totalQuantity === 0) return 'zeroQuantity';
    if (totalQuantity < expectedQuantity) return 'underQuantity';
    if (totalQuantity === expectedQuantity) return 'equalQuantity';
    return 'overQuantity';
  }, [
    primaryCampaign?.totalItemsQuantity,
    primaryCampaign?.expectedQuantity,
    storeDetails?.status
  ]);

  const dashboardMessage: DashboardMessageType = useMemo(() => {
    switch (primaryCampaign?.status) {
      case 'completed': {
        return DASHBOARD_MESSAGES.completedOrder;
      }
      case 'canceled': {
        return DASHBOARD_MESSAGES.canceledOrder;
      }
      case 'active': {
        if (storeDetails?.status === 'open') {
          if (closingSoon) {
            return ftux
              ? DASHBOARD_MESSAGES.openOrder.closingSoon.ftux
              : DASHBOARD_MESSAGES.openOrder.closingSoon[quantityStatus];
          }
          return ftux
            ? DASHBOARD_MESSAGES.openOrder.notClosingSoon.ftux
            : DASHBOARD_MESSAGES.openOrder.notClosingSoon[quantityStatus];
        }
        return DASHBOARD_MESSAGES.closedOrder[
          quantityStatus as QuantityStatusType
        ];
      }
      default: {
        return DASHBOARD_MESSAGES.default;
      }
    }
  }, [
    primaryCampaign?.status,
    storeDetails?.status,
    quantityStatus,
    closingSoon,
    ccoUrl,
    campaignOrderData
  ]);

  return dashboardMessage;
};

export default useDashboardMessage;
